import { green, red, blue, bold, yellow, whiteBright, gray } from "colorette";

type LogLevel = "log" | "info" | "success" | "error" | "warn" | "debug";

interface LoggerOptions {
  silent?: boolean;
  showPrefix?: boolean;
  showPrefixIcon?: boolean;
}

interface LogConfig {
  showPrefix?: boolean;
  showPrefixIcon?: boolean;
  addSpace?: string;
}

class Logger {
  private silent: boolean;
  private showPrefix: boolean;
  private showPrefixIcon: boolean;

  constructor(options?: LoggerOptions) {
    this.silent = options?.silent ?? false;
    this.showPrefix = options?.showPrefix ?? true;
    this.showPrefixIcon = options?.showPrefixIcon ?? true;
  }

  private prefix(
    level: LogLevel,
    showPrefix = true,
    showPrefixIcon = true
  ): string {
    const prefixText = showPrefix ? `[envx:${level}]` : "";

    // ![COMING_SOON_FEATURES] REMOVED from the code, but here for the possibility of future additions
    // const SPACE = "\u00A0";
    // const iconsUnicode = {
    //   info: "ℹ️",
    //   success: "✔" + SPACE,
    //   error: "❌" + SPACE,
    //   warn: "⚠️" + SPACE,
    //   debug: "🐞" + SPACE,
    // };

    const iconsText = {
      info: "[INFO]",
      success: "[OK]",
      error: "[ERR]",
      warn: "[WARN]",
      debug: "[DBG]",
    };
    const icon = showPrefixIcon ? iconsText[level] : "";

    const fullPrefix = `${prefixText} ${icon} `.trim();

    switch (level) {
      case "info":
      case "debug":
        return blue(bold(fullPrefix));
      case "success":
        return green(bold(fullPrefix));
      case "error":
        return red(bold(fullPrefix));
      case "warn":
        return yellow(bold(fullPrefix));
      default:
        return whiteBright(bold(prefixText));
    }
  }

  log(message: string, config?: LogConfig) {
    if (this.silent) return;
    const { showPrefix = false, addSpace = "" } = config || {};
    const _prefix = showPrefix ? whiteBright(bold("[envx]")) : "";
    console.log(`${addSpace}${_prefix} ${message}`.trim());
  }

  info(message: string, config?: LogConfig) {
    if (this.silent) return;
    const {
      showPrefix = this.showPrefix,
      showPrefixIcon = this.showPrefixIcon,
      addSpace = "",
    } = config || {};
    console.log(
      `${addSpace}${this.prefix("info", showPrefix, showPrefixIcon)} ${message}`
    );
  }

  success(message: string, config?: LogConfig) {
    if (this.silent) return;
    const {
      showPrefix = this.showPrefix,
      showPrefixIcon = this.showPrefixIcon,
      addSpace = "",
    } = config || {};
    console.log(
      `${addSpace}${this.prefix(
        "success",
        showPrefix,
        showPrefixIcon
      )} ${message}`
    );
  }

  error(message: string | Error, config?: LogConfig) {
    if (this.silent) return;
    const {
      showPrefix = this.showPrefix,
      showPrefixIcon = this.showPrefixIcon,
      addSpace = "",
    } = config || {};
    const msg = typeof message === "string" ? message : message.message;
    console.error(
      `${addSpace}${this.prefix("error", showPrefix, showPrefixIcon)} ${msg}`
    );
  }

  warn(message: string, config?: LogConfig) {
    if (this.silent) return;
    const {
      showPrefix = this.showPrefix,
      showPrefixIcon = this.showPrefixIcon,
      addSpace = "",
    } = config || {};
    console.warn(
      `${addSpace}${this.prefix("warn", showPrefix, showPrefixIcon)} ${message}`
    );
  }

  debug(message: string, config?: LogConfig) {
    if (this.silent) return;
    const {
      showPrefix = this.showPrefix,
      showPrefixIcon = this.showPrefixIcon,
      addSpace = "",
    } = config || {};
    console.debug(
      `${addSpace}${this.prefix(
        "debug",
        showPrefix,
        showPrefixIcon
      )} ${message}`
    );
  }

  block(message: string, prefix = "[envx]", config?: LogConfig) {
    if (this.silent) return;
    const { showPrefix = this.showPrefix, addSpace = "" } = config || {};
    const _prefix = showPrefix ? gray(bold(prefix)) : "";
    console.log(`\n${addSpace}${_prefix} ${bold(message)}`.trim());
  }
}

export const logger = new Logger({ silent: false });
